import {
  Box,
  FieldLabel,
  FieldSelect,
  FlexBox,
  Modal,
  ModalBody,
  ModalFoot,
  ModalHead,
  Text,
} from '@dixa/lib-shared-habla';
import { produce } from 'immer';
import { ampli } from 'lib-shared-tracking';
import type { Country, OpeningHours } from 'lib-shared-types-api';
import noop from 'lodash/noop';
import { useCallback, useState } from 'react';

import { DateCard } from 'components/settings/components/sections/openingHours/components/dateCard/dateCard';

import type { ModifiedListItem } from '../../../../schedule.context';

import styles from './recoverHolidaysModal.module.scss';

type Props = {
  country: Country;
  holidays: OpeningHours[];
  onClose: VoidFunction;
  onSubmit: (exceptions: ModifiedListItem) => void;
};

export const RecoverHolidaysModal = ({
  country,
  holidays,
  onClose,
  onSubmit,
}: Props): JSX.Element => {
  const { name, country_code: id } = country;

  const [selectedHolidays, setSelectedHolidays] = useState<ModifiedListItem>(
    {},
  );

  const handleDateClick = useCallback(
    (holiday: OpeningHours) => () =>
      void setSelectedHolidays(
        produce((selectedHolidays: ModifiedListItem) => {
          if (selectedHolidays[holiday.id]) {
            delete selectedHolidays[holiday.id];
          } else {
            selectedHolidays[holiday.id] = holiday;
          }
        }),
      ),
    [],
  );

  const handleSubmit = useCallback(() => {
    ampli.businessHoursScheduleRecoverHolidaysFormSubmitted();
    onSubmit(selectedHolidays);
    onClose();
  }, [selectedHolidays, onClose, onSubmit]);

  const handleCancelModal = useCallback(() => {
    ampli.businessHoursScheduleRecoverHolidaysFormCancelled();
    onClose();
  }, [onClose]);

  return (
    <Modal isOpen onClose={handleCancelModal}>
      <Box width="600px">
        <ModalHead onClose={handleCancelModal} title="Recover deleted holidays">
          <Text.Paragraph subdued variant="2">
            This allows you to recover holidays from the holiday calendar you
            deleted previously (but not your own exceptions). Recovered holidays
            are set as “closed”.
          </Text.Paragraph>
        </ModalHead>
        <ModalBody>
          <FieldSelect
            disabled
            input={{
              name: 'holiday-calendar',
              value: id,
              onChange: noop,
            }}
            items={[{ id, name }]}
            label="Holiday calendar"
          />
          <FlexBox flexDirection="column" gap="200" margin="300 0 0">
            <FieldLabel>Deleted holidays</FieldLabel>
            <div className={styles.dateWrapper}>
              {holidays.map((holiday) => (
                <DateCard
                  key={holiday.id}
                  exception={holiday}
                  isSelected={!!selectedHolidays[holiday.id]}
                  onClick={handleDateClick(holiday)}
                />
              ))}
            </div>
          </FlexBox>
        </ModalBody>
        <ModalFoot
          cancelOnClick={handleCancelModal}
          confirmProps={{ disabled: !Object.keys(selectedHolidays).length }}
          confirmName="Recover selected holidays"
          confirmOnClick={handleSubmit}
        />
      </Box>
    </Modal>
  );
};
